/**
 * Filter Module
 * 
 * This module handles the filtering of posts on the Projects & Insights archive pages.
 */

import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';

let _selectors = {
    postIndex: '.archive-filter',
    regionFilter: '.js-region',
    capabilityFilter: '.js-capability',
    keywordFilter: '.js-keyword',
    formReset: '.js-form-reset',
    filterResponse: '.js-insert-posts',
    pagination: '.js-pagination',
    paginationLinks: '.js-page-numbers',
    resultsCount: '.archive-filter_results',
};

// Cached Elements.
let _el = {};

// Module Variables
let _vars = {};

// Debounce function to limit how often a function can be called
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

// Module init
const _init = () => {
    // Define & cache elements
    _setElements();
    _bindEvents();

    if(_el.postIndex.length > 0) {
        // JS filter posts on init. The filtered posts should already be shown with PHP,
        // but this helps if the user has arrived via the browser back button.
        // Checks URL vars and filters posts accordingly
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const filter = {
            capability: pathSegments.includes('capability') ? pathSegments[pathSegments.indexOf('capability') + 1] : null,
            region: pathSegments.includes('region') ? pathSegments[pathSegments.indexOf('region') + 1] : null,
            page: pathSegments.includes('page') ? parseInt(pathSegments[pathSegments.indexOf('page') + 1]) : 1
        };
        handleFilters(false, filter);
    }
};

const _bindEvents = () => {

    // Attach pagination handler using event delegation
    $(_el.pagination).on('click', 'a', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        const pageNum = $(this).data('page');
        handleFilters(true, {
            page: pageNum
        });
    });
    
    // Prevent form submission and handle keyword filter
    $(_el.postIndex).on('submit', function(e) {
        e.preventDefault();
        handleFilters();
    });
    
    // Handle dropdowns
    $('.js-region, .js-capability').on("change", function() {
        handleFilters();
    });
    
    // Apply debounce to keyword filter
    $(_el.keywordFilter).on("keyup", debounce(function() {
        handleFilters();
    }, 500)); // 500ms delay
    
    // Handle form reset
    $(_el.formReset).on("click", function() {
        // Make sure form has reset before running filters
        setTimeout(() => {
            handleFilters();
        }, 500);
    });
};
 
const _scrollToTop = () => {
    _el.filterResponse[0].scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
    });
}

// Shared function to fetch filtered posts
const _fetchFilteredPosts = (scrollToTop = true, filter = null) => {
    let filterEl = $('.archive-filter');
    let postType = filterEl.data('type');
    let postBase = filterEl.data('base');
    
    // Get selected term slugs - use provided filter values if available, otherwise get values from the form
    let selectedCapability = filter && 'capability' in filter ? filter.capability : filterEl.find(_el.capabilityFilter).val();
    let selectedRegion = filter && 'region' in filter ? filter.region : filterEl.find(_el.regionFilter).val();
    let pageNum = filter && 'page' in filter ? filter.page : 1;

    let keyword = filterEl.find(_el.keywordFilter).val();
    let postsPerPage = postType === 'project' ? 12 : 8;

    // Build query parameters
    const queryParams = {
        page: pageNum,
        _embed: true, // Include featured image data
        per_page: postsPerPage
    };

    // Add filters if they exist
    if (selectedCapability) {
        queryParams.capability = selectedCapability;
    }
    if (selectedRegion) {
        queryParams.region = selectedRegion;
    }
    if (keyword) {
        queryParams.search = keyword;
    }

    // Show loading state
    $(_el.filterResponse).addClass('loading-posts');

    // Build the API path with query parameters
    const path = addQueryArgs( `/wp-json/wp/v2/${postType}`, queryParams );
            
    let totalPosts, totalPages;

    apiFetch({ path, parse: false })
        .then(response => {
            
            // Get total posts and pages from the response headers
            totalPosts = parseInt(response.headers.get('X-WP-Total') || response.length);
            totalPages = parseInt(response.headers.get('X-WP-TotalPages') || 1);

            return response.json();
        })
        .then(response => {
            
            if(scrollToTop) {
                _scrollToTop();
            }

            formatPostsHtml(response, postType).then(formattedResponse => {

                // Build URL
                let pathname = postBase.replace(/\/$/, '');
                if (selectedCapability) {
                    pathname += `/capability/${selectedCapability}`; // Add capability if selected
                }
                if (selectedRegion) {
                    pathname += `/region/${selectedRegion}`; // Add region if selected
                }
                if (pageNum > 1) {
                    pathname += `/page/${pageNum}`; // Add page number if not page 1
                }
                pathname += '/'; // Ensure trailing slash
                
                // Replace the URL
                window.history.pushState({}, '', pathname);

                // Insert posts html
                $(_el.filterResponse).html(formattedResponse).promise().done(function() {
                    $(_el.filterResponse).removeClass('loading-posts');
                    addPagination(pageNum, totalPages);
                    updateResultsCount(filterEl, totalPosts);
                }).fail(function(error) {
                    console.error('Error updating content:', error);
                    $(_el.filterResponse).removeClass('loading-posts');
                });
            });
        })
        .catch(error => {
            console.error('REST API Error:', error);
            $(_el.filterResponse).removeClass('loading-posts');
        });

    return false;
};

const handleFilters = _fetchFilteredPosts;

// Insert page number links
const addPagination = (currentPage, totalPages) => {
    // Insert page number links
    let html = "";
    for (let i = 1; i <= totalPages; i++) {
        html += `<li class="${i === currentPage ? 'current' : ''}"><a href="#" title="Page ${i}" data-page="${i}">${i}</a></li>`;
    }
    $(_el.paginationLinks).html(html);

    // Other buttons are always in the markup (we listen for clicks on parent)
    $('.pagination-next').data('page', currentPage + 1);
    $('.pagination-prev').data('page', currentPage - 1);
    $('.pagination-prev, .pagination-first').attr('disabled', function(){return currentPage === 1;});
    $('.pagination-next, .pagination-last').attr('disabled', function(){return currentPage === totalPages;});
}

// Helper function to format REST API response into HTML
const formatPostsHtml = async (response, postType) => {

    // Helper function to generate image HTML from media data
    const generateImageHtml = (mediaData) => {
        if (!mediaData || !mediaData.media_details || !mediaData.media_details.sizes) {
            return '<img src="' + ajax_posts.image_placeholder + '" />';
        }

        return `
            <img 
                width="${mediaData.media_details.sizes.full.width}" 
                height="${mediaData.media_details.sizes.full.height}" 
                src="${mediaData.source_url}" 
                class="attachment-post-thumbnail size-post-thumbnail wp-post-image" 
                alt="${mediaData.alt_text || ''}" 
                decoding="async" 
                loading="lazy" 
                srcset="${Object.entries(mediaData.media_details.sizes)
                    .map(([key, size]) => `${size.source_url} ${size.width}w`)
                    .join(', ')}" 
                sizes="(max-width: ${mediaData.media_details.width}px) 100vw, ${mediaData.media_details.width}px"
            >
        `;
    };

    let html = "";
    if (response && response.length > 0) {
        // Convert to async/await for cleaner handling of multiple promises
        const postsHtml = await Promise.all(response.map(async post => {
            let imageHtml = '<img src="' + ajax_posts.image_placeholder + '" />';
            
            try {
                // Check for source_site meta field
                const sourceSite = post.meta.source_blog_id;
                const sourcePost = post.meta.source_post_id;
                
                if (sourceSite && sourcePost) {
                    // Get the source site URL from the global variable
                    const sourceSiteUrl = ajax_posts.site_urls[sourceSite];
                    if (!sourceSiteUrl) {
                        console.warn('Source site URL not found for site ID:', sourceSite);
                        return;
                    }

                    // First fetch the source post to get its featured media ID
                    const sourcePostPath = `${sourceSiteUrl}/wp-json/wp/v2/${postType}/${sourcePost}`;
                    const sourcePostData = await apiFetch({ path: sourcePostPath });

                    if (sourcePostData && sourcePostData.featured_media) {
                        // Now fetch the featured image using the featured_media ID from the source post
                        const sourcePath = `${sourceSiteUrl}/wp-json/wp/v2/media/${sourcePostData.featured_media}`;
                        const sourceImage = await apiFetch({ path: sourcePath });
                        imageHtml = generateImageHtml(sourceImage);
                    }
                } else {
                    // Use existing featured image logic for current site
                    const featuredImage = post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0];
                    imageHtml = generateImageHtml(featuredImage);
                }
            } catch (error) {
                console.warn('Error processing featured image for post:', post.id, error);
            }

            const isProject = post.type === 'project';
            const isFeatured = post.meta && post.meta['featured-post'] === 'true' ? true : false;
            
            // Build class string with conditional modifiers
            const postClass = [
                'post-item',
                !isProject ? 'is-outlined' : '',
                isFeatured ? 'is-featured-post' : ''
            ].filter(Boolean).join(' ');

            return `
                <a href="${post.link}" class="${postClass}">
                    <figure class="post-item_image">${imageHtml}</figure>
                    <h2 class="post-item_heading is-style-prose">${post.title.rendered}</h2>
                </a>
            `;
        }));

        html = postsHtml.join('');
    } else {
        html += '<p>Sorry, no results found.</p>';
    }

    return html;
};

// Update results count function to use total posts from response
// For Insights, we've hidden Region and Capability filters on client's request
const updateResultsCount = (filter, totalPosts) => {
    let searchVal = filter.find(_el.keywordFilter).val();
    let selectedCapability = filter.find(_el.capabilityFilter).val();
    let selectedRegion = filter.find(_el.regionFilter).val();
    let resultTarget = $(_el.resultsCount);

    let searchValue = searchVal ? "'" + searchVal + "'" : '';
    let capabilityValue = selectedCapability ? "'" + selectedCapability + "'" : '';
    let regionValue = selectedRegion ? "'" + selectedRegion + "'" : '';

    let firstSplit = (searchVal && capabilityValue) || (searchVal && regionValue) ? ', ' : '';
    let secondSplit = capabilityValue && regionValue ? ', ' : '';

    let fillerText = totalPosts === 1 ? ' insight found</span> with keywords ' : ' insights found</span> with keywords ';

    if(resultTarget) {
        if(searchVal !== '' || selectedCapability || selectedRegion) {
            $(resultTarget).html(
                '<p><span>'
                    + totalPosts
                    + fillerText
                    + searchValue
                    + firstSplit
                    + capabilityValue
                    + secondSplit
                    + regionValue
                + '.</p>'
            );
            $(resultTarget).addClass('initialised');
        } else {
            $(resultTarget).removeClass('initialised');
            $(resultTarget).html('');
        }
    }
}

// Set/Reset and Cache Module elements
const _setElements = () => {
    // Cache Elements into _el & create variable to check existence
    Object.keys(_selectors).forEach(key => {
        const el = document.querySelectorAll(_selectors[key]);
        _el[key] = el;
        _vars[key] = el.length;
    });
};

const Filter = {
    init: _init,
};

export default Filter;